import { UrlManager } from '@providers/urlManager';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const useChatbotStore = create(
    persist(
        set => ({
            messages: [],
            trendingTopics: [],
            tipsOfTheDay: [],
            employeeTimeOff: [],
            requisitionList: [],
            threadId: null,
            userInfo: null,
            isLoading: false,
            uploadProgress: null,
            uploadError: null,
            imagePreview: null,
            isFileUploaded: false,
            fileName: '',
            isUploadedFileLoading: false,

            sendMessage: async message => {
                set({ isLoading: true });
                const state = useChatbotStore.getState();

                const payload = {
                    thread_id: state.threadId,
                    user: state.userInfo,
                    history: [...state.messages, { sender: 'user', message }]
                        .map(msg => ({
                            user: msg.sender === 'user' ? msg.message : undefined,
                            bot: msg.sender === 'bot' ? msg.message : undefined,
                        }))
                        .filter(msg => msg.user || msg.bot),
                    approach: 'rrr',
                    overrides: {
                        retrieval_mode: 'hybrid',
                        semantic_ranker: true,
                        semantic_captions: false,
                        top: 3,
                        suggest_followup_questions: false,
                    },
                };

                set(state => ({
                    messages: [...state.messages, { sender: 'user', message, id: null }],
                }));

                try {
                    const response = await fetch('/chat', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                    });
                    const data = await response.json();

                    if (data.error) {
                        set(state => ({
                            messages: [
                                ...state.messages,
                                {
                                    sender: 'bot',
                                    message: `**Oops Error:**\n ${data.error}`,
                                },
                            ],
                            isLoading: false,
                        }));
                        return data;
                    }

                    set(state => ({
                        messages: [
                            ...state.messages,
                            { sender: 'bot', message: data.answer, id: data.id },
                        ],
                        threadId: data.thread_id,
                        isLoading: false,
                    }));

                    return data;
                } catch (error) {
                    set({ isLoading: false });
                    console.error(error);
                }
            },

            setUserInfo: userInfo => {
                set({ userInfo });
            },

            initializeChat: (threadId, userInfo) => {
                set({
                    threadId,
                    userInfo,
                    messages: [{ sender: 'bot', message: 'Hello! How can I assist you today?' }],
                });
            },

            postChatFeedback: async (feedback, messageId, flag) => {
                try {
                    const response = await fetch(UrlManager.getChatFeedbackUrl(), {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            id: messageId,
                            flag: flag,
                            feedback: feedback || null,
                        }),
                    });
                    return response.json();
                } catch (error) {
                    console.error(error);
                }
            },

            getTrendingTopics: async () => {
                try {
                    const response = await fetch('/trending_topic');
                    const data = await response.json();
                    set({ trendingTopics: data });
                    return data;
                } catch (error) {
                    console.error(error);
                }
            },

            getTipsOfTheDay: async () => {
                try {
                    const response = await fetch('/tips');
                    const data = await response.json();
                    set({ tipsOfTheDay: data });
                    return data;
                } catch (error) {
                    console.error(error);
                }
            },

            getEmployeeTimeOff: async payload => {
                try {
                    const response = await fetch('/employee-time-off', {
                        method: 'POST',
                        body: JSON.stringify(payload),
                    });
                    const data = await response.json();
                    set({ employeeTimeOff: data });
                    return data;
                } catch (error) {
                    console.error(error);
                }
            },

            getRequisitionList: async () => {
                try {
                    const response = await fetch('/requisitions');
                    const data = await response.json();
                    set({ requisitionList: data });
                    return data;
                } catch (error) {
                    console.error(error);
                }
            },

            uploadFile: async file => {
                if (!file) return;

                if (!file.type.startsWith('image/')) {
                    set({ uploadError: 'Only image files are allowed.' });
                    return;
                }

                set({
                    fileName: file.name,
                    isLoading: true,
                });

                // Create preview
                const reader = new FileReader();
                reader.onload = event => {
                    set({ imagePreview: event.target.result });
                };
                reader.readAsDataURL(file);

                try {
                    const formData = new FormData();
                    formData.append('file', file);

                    const response = await fetch('/extract-net-speed', {
                        method: 'POST',
                        body: formData,
                    });

                    const data = await response.json();
                    set({ isFileUploaded: true });

                    return data.net_speed;
                } catch (error) {
                    set({ uploadError: 'Error uploading file.' });
                    console.error('Error uploading file:', error);
                } finally {
                    set({ isLoading: false });
                }
            },

            clearUpload: () => {
                set({
                    imagePreview: null,
                    fileName: '',
                    uploadProgress: null,
                    isFileUploaded: false,
                    uploadError: null,
                });
            },
            clearChatMessages: () => {
                set({
                    messages: [{ sender: 'bot', message: 'Hello! How can I assist you today?' }],
                    isLoading: false,
                    threadId: null,
                });
            },
        }),
        {
            name: 'chatbot-storage',
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
);

export default useChatbotStore;
